import React from 'react';
import {TextDisplay, TextDisplayProps} from "./TextDisplay";
import {formatNumber, Locale} from "../../../helpers/Formatting";

/**
 * Represents the properties for rendering a localized numerical value as part of a display component.
 * This interface extends the properties of a text-based display to support numeric formatting.
 *
 * The purpose is to enable developers to present numbers in a user-friendly, localized format
 * with optional handling for percentages in applications where internationalization is required.
 *
 * @extends TextDisplayProps Inherits text display properties like font, alignment, and other common display behaviors.
 */
export interface NumberDisplayProps extends TextDisplayProps {
    locale?: Locale
    value: number
    isPercent?: boolean
}

/**
 * Renders a formatted numerical value with optional percentage display.
 *
 * This function is primarily used for displaying numeric data in a user-friendly format
 * based on locale-specific conventions. It supports customization options such as alignment
 * and optional percentage indicators, enabling seamless integration within various UI components.
 *
 * @param props Object containing configuration properties for rendering the number.
 * @param props.value The numeric value to be formatted and displayed.
 * @param props.locale An optional locale setting that determines the formatting style. Defaults to `Locale.GERMANY`.
 * @param props.align An optional alignment setting for the displayed text. Defaults to `'right'`.
 * @param props.isPercent A boolean flag indicating whether to append a percentage symbol ('%') to the number. Defaults to `false`.
 *
 * @return A rendered UI component (`TextDisplay`) presenting the formatted number.
 */
export function NumberDisplay( props: NumberDisplayProps ){

    return (
        <TextDisplay { ...props } align={ props.align ?? 'right' }>
            {
                formatNumber(
                    props.value,
                    props.locale ?? Locale.GERMANY
                )
            }
            { props.isPercent ? ' %' : '' }
        </TextDisplay>
    );
}